import * as React from "react";
import { NavBar } from "../../common/NavBar";
import { Dispatcher } from "../Sites/SiteAboutPage/SiteAboutPageUtils/siteAboutInterfaces";
import { useEditLineCrossingsStore } from "./editLineCrossingsStore/editLineCrossingsStore";
import { LineDrawer } from "./LineDrawer/LineDrawer";
import { DirectionSelector } from "./DirectionSelector/DirectionSelector";
import { LineCrossingsBreadcrumbs } from "./LineCrossingsBreadcrumbs";
import { createLineCrossingsLinksArray } from "./EditLineCrossingsUtils/createLineCrossingsLinksArray";
import { useNavigate } from "react-router-dom";
import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
} from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import DeleteIcon from "@mui/icons-material/Delete";
import { OrangeTextButton } from "../../common/OrangeTextButton";
import { EditViewSyncButton } from "../../common/EditViewSyncButton";
import { SyncNeededBanner } from "../../common/SyncNeededBanner";
import { ActuateButton } from "../../common/ActuateButton";
import BackdropLoadingScreen from "../../common/BackdropLoadingScreen";
import { getAllCameras } from "../EditCamera/EditCameraUtils/getAllCameras";
import {
  getNumbersAfterEditCamera,
  getNumbersAfterSites,
} from "../EditCamera/EditCamera";
import { LineOverlay } from "./EditLineCrossingsUtils/LineOverlay";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { DeleteLineCrossingDialog } from "./DeleteLineCrossingDialog";
import { lineCrossingsPresetSearch } from "./EditLineCrossingsUtils/lineCrossingsListPath";

/** Select value for the camera-wide Default configuration (no preset). */
const DEFAULT_CONFIGURATION_OPTION = "__default__";

export const EditLineCrossings = ({
  navBarCollapsed,
  setNavBarCollapsed,
}: {
  navBarCollapsed: boolean;
  setNavBarCollapsed: Dispatcher<boolean>;
}) => {
  const {
    pageLoading,
    editorVisible,
    setEditorVisible,
    breadcrumbs,
    cameraInfo,
    dimensions,
    imageURL,
    setImageURL,
    setPageLoading,
    setEditsMade,
    getAllEditLineCrossingsPageInfo,
    lines,
    setLines,
    originalLines,
    setOriginalLines,
    setSelectedLineIndex,
    metricLabels,
    handlePostLineCrossings,
    setDeleteLineCrossingDialogOpen,
    setLineToDeleteId,
    lineCrossingActive,
    selectedPresetId,
    cameraPresets,
    switchConfiguration,
  } = useEditLineCrossingsStore();

  const lineCrossingProductEnabled =
    lineCrossingActive.intruder || lineCrossingActive.vehicle;

  const navigate = useNavigate();

  const siteId = Number(getNumbersAfterSites(location.href));

  const [allCamerasData, setAllCamerasData] = React.useState<any[]>([]);
  const [cameraIndex, setCameraIndex] = React.useState(0);
  const [largerMode, setLargerMode] = React.useState(false);

  const handleSitesBreadcrumbClick = (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ) => {
    event.preventDefault();
    localStorage.setItem("level0Click", "false");
    localStorage.setItem("navigatedFromSitesAboutPage", "false");
    navigate("/sites");
  };

  const handleSingularSiteBreadcrumbClick = () => {
    navigate(`/sites/aboutsite/${siteId}`);
  };

  // Configuration switcher (ENG-1610): Default (camera-wide lines) plus the
  // camera's named presets. Mirrors the flex preset picker on the Site About
  // camera row; `?preset=` on the route pre-selects it on entry.
  const showConfigurationSwitcher =
    cameraPresets.length > 0 || selectedPresetId != null;
  const selectedConfigurationValue =
    selectedPresetId == null
      ? DEFAULT_CONFIGURATION_OPTION
      : String(selectedPresetId);
  const selectedPresetMissingFromList =
    selectedPresetId != null &&
    !cameraPresets.some((preset) => preset.id === selectedPresetId);

  const handleConfigurationChange = (value: string) => {
    if (editorVisible) {
      return; // unsaved edits belong to the current configuration
    }
    const nextPresetId =
      value === DEFAULT_CONFIGURATION_OPTION ? null : Number(value);
    if (nextPresetId === selectedPresetId) {
      return;
    }
    // Keep the URL in step so a refresh (or the back button) reopens the same
    // configuration; the camera id in the path is unchanged.
    navigate(
      { search: lineCrossingsPresetSearch(nextPresetId) },
      { replace: true }
    );
    void switchConfiguration(navigate, nextPresetId);
  };

  const handleEditAddLinesClick = () => {
    setLargerMode(false);
    // Store current lines as original (for cancel functionality)
    // Deep copy to prevent reference issues when editing coordinates
    setOriginalLines(
      lines.map((line) => ({
        ...line,
        coordinates: [[...line.coordinates[0]], [...line.coordinates[1]]] as [
          [number, number],
          [number, number]
        ],
        metric_labels: [...line.metric_labels],
      }))
    );
    setEditorVisible(true);
  };

  const handleSaveClick = () => {
    setLargerMode(false);
    // Save lines to API (POST new lines, PATCH modified lines)
    setPageLoading(true);
    setSelectedLineIndex(null);
    handlePostLineCrossings(navigate);
  };

  const handleCancelClick = () => {
    // Reset lines to original state and exit edit mode
    // Deep copy to prevent reference issues
    setLines(
      originalLines.map((line) => ({
        ...line,
        coordinates: [[...line.coordinates[0]], [...line.coordinates[1]]] as [
          [number, number],
          [number, number]
        ],
        metric_labels: [...line.metric_labels],
      }))
    );
    setEditorVisible(false);
    setSelectedLineIndex(null);
    setEditsMade(false);
    setLargerMode(false);
  };

  React.useEffect(() => {
    getAllEditLineCrossingsPageInfo(navigate);
    getAllCameras(
      Number(getNumbersAfterSites(location.href)),
      Number(getNumbersAfterEditCamera(location.href)),
      setAllCamerasData,
      setCameraIndex
    );
    localStorage.setItem("siteId", getNumbersAfterSites(location.href));
    localStorage.setItem(
      "currentCameraId",
      getNumbersAfterEditCamera(location.href)
    );
  }, [getAllEditLineCrossingsPageInfo, navigate]);

  // Add effect to handle camera changes
  React.useEffect(() => {
    const currentCameraId = getNumbersAfterEditCamera(location.href);
    if (currentCameraId) {
      getAllEditLineCrossingsPageInfo(navigate);
    }
  }, [getAllEditLineCrossingsPageInfo, navigate]);

  return (
    <div>
      <SyncNeededBanner navigate={navigate} />
      <DeleteLineCrossingDialog />
      <div className="bg-actuate-grey absolute w-full h-full overflow-scroll">
        <BackdropLoadingScreen openState={pageLoading} />
        {allCamerasData[0] && (
          <div>
            {/* Breadcrumbs bar */}
            <div
              className={
                !navBarCollapsed
                  ? "absolute top-[15px] left-[150px] right-[1px] h-[59px] bg-white"
                  : "absolute top-[15px] left-[75px] right-[1px] h-[59px] bg-white"
              }
            >
              <LineCrossingsBreadcrumbs
                linksArray={createLineCrossingsLinksArray(breadcrumbs)}
                handleSitesBreadcrumbClick={handleSitesBreadcrumbClick}
                handleSingularSiteBreadcrumbClick={
                  handleSingularSiteBreadcrumbClick
                }
                styling="absolute top-[15px] left-[30px]"
              />
            </div>
            {/* Camera name header */}
            <div
              className={
                !navBarCollapsed
                  ? "absolute top-[87px] left-[150px] right-[1px] h-[58px] bg-white"
                  : "absolute top-[87px] left-[75px] right-[1px] h-[58px] bg-white"
              }
            >
              <div className="flex items-start gap-[24px]">
                <p className="font-black font-mulish text-[20px] p-3 indent-4">
                  {cameraInfo.camera_name}
                </p>
                {showConfigurationSwitcher && (
                  <Tooltip
                    title={
                      editorVisible
                        ? "Save or cancel your line edits before switching configuration"
                        : ""
                    }
                    placement="bottom"
                  >
                    <span>
                      <FormControl
                        size="small"
                        sx={{ minWidth: 240, maxWidth: 560, mt: "9px", fontFamily: "Mulish" }}
                      >
                        <InputLabel
                          id="line-crossings-configuration-label"
                          sx={{ fontFamily: "Mulish" }}
                        >
                          Configuration
                        </InputLabel>
                        <Select
                          labelId="line-crossings-configuration-label"
                          id="line-crossings-configuration-select"
                          label="Configuration"
                          value={selectedConfigurationValue}
                          disabled={editorVisible}
                          onChange={(event) =>
                            handleConfigurationChange(String(event.target.value))
                          }
                          sx={{ fontFamily: "Mulish", bgcolor: "#fff" }}
                        >
                          <MenuItem
                            value={DEFAULT_CONFIGURATION_OPTION}
                            sx={{ fontFamily: "Mulish" }}
                          >
                            Default
                          </MenuItem>
                          {cameraPresets.map((preset) => (
                            <MenuItem
                              key={preset.id}
                              value={String(preset.id)}
                              sx={{ fontFamily: "Mulish" }}
                            >
                              {preset.name}
                            </MenuItem>
                          ))}
                          {selectedPresetMissingFromList && (
                            <MenuItem
                              value={String(selectedPresetId)}
                              sx={{ fontFamily: "Mulish" }}
                            >
                              {`Preset #${selectedPresetId}`}
                            </MenuItem>
                          )}
                        </Select>
                        <FormHelperText sx={{ fontFamily: "Mulish", mx: 0 }}>
                          {selectedPresetId == null
                            ? "Default lines run whenever no configuration is assigned to the active flex schedule. "
                            : "These lines run only while a flex schedule assigned to this configuration is active. "}
                          Saved changes reach the camera after the site is synced.
                        </FormHelperText>
                      </FormControl>
                    </span>
                  </Tooltip>
                )}
              </div>
            </div>
            {/* Sync control */}
            <div className="absolute top-[97px] right-[100px]">
              {!editorVisible && <EditViewSyncButton navigate={navigate} />}
            </div>
            {/* Main content area */}
            <div
              className={
                !navBarCollapsed
                  ? "absolute top-[145px] left-[150px] right-[1px] h-full bg-white"
                  : "absolute top-[145px] left-[75px] right-[1px] h-full bg-white"
              }
            >
              {/* Previous camera arrow */}
              {!editorVisible && allCamerasData.length > 1 && (
                <div className="pt-[200px] w-[40px]">
                  <Tooltip
                    title={`Previous Camera: ${
                      cameraIndex === 0
                        ? allCamerasData[allCamerasData.length - 1].camera_name
                        : allCamerasData[cameraIndex - 1].camera_name
                    }`}
                    placement="top"
                    sx={{ fontSize: "12px", fontFamily: "mulish" }}
                  >
                    <div>
                      <OrangeTextButton
                        onClick={() => {
                          const targetCameraId =
                            cameraIndex === 0
                              ? allCamerasData[allCamerasData.length - 1].id
                              : allCamerasData[cameraIndex - 1].id;

                          navigate(
                            `/sites/${siteId}/editcamera/${targetCameraId}/editlinecrossings`
                          );

                          // Reset state and reload data for the new camera
                          setPageLoading(true);
                          setEditorVisible(false);
                          setEditsMade(false);
                          setImageURL(""); // Clear image URL immediately

                          // Reload camera data
                          getAllCameras(
                            siteId,
                            targetCameraId,
                            setAllCamerasData,
                            setCameraIndex
                          );

                          // Reload page info for the new camera
                          setTimeout(() => {
                            getAllEditLineCrossingsPageInfo(navigate);
                          }, 100);
                        }}
                        text={
                          <ArrowBackIosIcon fontSize="large" id="backButton" />
                        }
                        disabled={allCamerasData.length < 2}
                      />
                    </div>
                  </Tooltip>
                </div>
              )}
              {/* Next camera arrow */}
              {!editorVisible && allCamerasData.length > 1 && (
                <div className="absolute top-[200px] left-[1300px]">
                  <Tooltip
                    title={`Next Camera: ${
                      cameraIndex + 1 === allCamerasData.length
                        ? allCamerasData[0].camera_name
                        : allCamerasData[cameraIndex + 1].camera_name
                    }`}
                    placement="top"
                    sx={{ fontSize: "12px", fontFamily: "mulish" }}
                  >
                    <div>
                      <OrangeTextButton
                        onClick={() => {
                          const targetCameraId =
                            cameraIndex + 1 === allCamerasData.length
                              ? allCamerasData[0].id
                              : allCamerasData[cameraIndex + 1].id;

                          navigate(
                            `/sites/${siteId}/editcamera/${targetCameraId}/editlinecrossings`
                          );

                          // Reset state and reload data for the new camera
                          setPageLoading(true);
                          setEditorVisible(false);
                          setEditsMade(false);
                          setImageURL(""); // Clear image URL immediately

                          // Reload camera data
                          getAllCameras(
                            siteId,
                            targetCameraId,
                            setAllCamerasData,
                            setCameraIndex
                          );

                          // Reload page info for the new camera
                          setTimeout(() => {
                            getAllEditLineCrossingsPageInfo(navigate);
                          }, 100);
                        }}
                        text={
                          <ArrowForwardIosIcon
                            fontSize="large"
                            id="backButton"
                          />
                        }
                        disabled={allCamerasData.length < 2}
                      />
                    </div>
                  </Tooltip>
                </div>
              )}
              {/* Image preview with lines overlay and Edit button (non-edit mode) */}
              {!editorVisible && (
                <div className="absolute top-[53px] left-[70px]">
                  {/* Edit/Add Lines button - top right of image (only show if product is enabled) */}
                  {lineCrossingProductEnabled && (
                    <div
                      style={{
                        position: "absolute",
                        top: -38,
                        right: 0,
                      }}
                    >
                      <OrangeTextButton
                        onClick={handleEditAddLinesClick}
                        text="Edit/Add Lines"
                        disabled={false}
                      />
                    </div>
                  )}
                  <div
                    style={{
                      position: "relative",
                      width: dimensions.width,
                      height: dimensions.height,
                    }}
                  >
                    <img
                      src={imageURL}
                      style={{
                        width: `${dimensions.width}px`,
                        height: `${dimensions.height}px`,
                      }}
                      alt="Camera Preview"
                    />
                    {lineCrossingProductEnabled && (
                      <LineOverlay
                        lines={lines}
                        width={dimensions.width}
                        height={dimensions.height}
                      />
                    )}
                  </div>
                </div>
              )}
              {/* Active Line Crossings header (non-edit mode, only if product enabled) */}
              {!editorVisible && lineCrossingProductEnabled && (
                <div
                  className={
                    !navBarCollapsed
                      ? "absolute left-[775px] top-[20px] font-mulish text-[18px] font-bold text-actuate-blue"
                      : "absolute left-[680px] top-[20px] font-mulish text-[18px] font-bold text-actuate-blue"
                  }
                >
                  Active Line Crossings
                </div>
              )}
              {/* Lines table (non-edit mode, only if product enabled) */}
              {!editorVisible && lineCrossingProductEnabled && (
                <div
                  className={
                    !navBarCollapsed
                      ? "absolute left-[775px] top-[73px]"
                      : "absolute left-[680px] top-[73px]"
                  }
                >
                  {lines.length > 0 && (
                    <Paper sx={{ width: 400, maxHeight: 360 }}>
                      <TableContainer sx={{ maxHeight: 360 }}>
                        <Table
                          sx={{
                            fontFamily: "Mulish",
                            tableLayout: "auto",
                          }}
                          aria-label="lines table"
                          size="small"
                        >
                          <TableHead>
                            <TableRow>
                              <TableCell
                                sx={{
                                  fontWeight: "bold",
                                  width: "120px",
                                  padding: "8px",
                                  paddingLeft: "18px",
                                }}
                              >
                                Line Color
                              </TableCell>
                              <TableCell
                                sx={{
                                  fontWeight: "bold",
                                  padding: "8px",
                                }}
                              >
                                Metrics
                              </TableCell>
                              <TableCell
                                sx={{
                                  fontWeight: "bold",
                                  width: "50px",
                                  padding: "8px",
                                }}
                              ></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {lines.map((line, index) => (
                              <TableRow key={`line-row-${index}`}>
                                <TableCell
                                  sx={{
                                    width: "120px",
                                    padding: "8px",
                                    paddingLeft: "18px",
                                  }}
                                >
                                  <div
                                    style={{
                                      height: 25,
                                      width: 25,
                                      background: line.color,
                                      borderRadius: 4,
                                    }}
                                  ></div>
                                </TableCell>
                                <TableCell sx={{ padding: "8px" }}>
                                  <div
                                    style={{
                                      maxWidth: "200px",
                                    }}
                                  >
                                    {(() => {
                                      const displayLabels: string[] = [];
                                      const hasIntruder =
                                        line.metric_labels.some(
                                          (id) =>
                                            metricLabels.find(
                                              (m) => m.id === id
                                            )?.label_name ===
                                            "intruder_line_crossing"
                                        );
                                      const hasVehicle =
                                        line.metric_labels.some(
                                          (id) =>
                                            metricLabels.find(
                                              (m) => m.id === id
                                            )?.label_name ===
                                            "vehicle_line_crossing"
                                        );
                                      const hasBike = line.metric_labels.some(
                                        (id) =>
                                          metricLabels.find((m) => m.id === id)
                                            ?.label_name ===
                                          "bike_line_crossing"
                                      );

                                      if (hasIntruder)
                                        displayLabels.push("Intruder");
                                      if (hasVehicle || hasBike)
                                        displayLabels.push("Vehicle/Bike");

                                      return displayLabels.join(", ");
                                    })()}
                                  </div>
                                </TableCell>
                                <TableCell
                                  sx={{
                                    width: "50px",
                                    padding: "8px",
                                  }}
                                >
                                  <Tooltip
                                    title="Delete"
                                    placement="top"
                                    sx={{
                                      fontSize: "15px",
                                      fontFamily: "mulish",
                                    }}
                                  >
                                    <span>
                                      <OrangeTextButton
                                        onClick={() => {
                                          if (line.id) {
                                            setLineToDeleteId(line.id);
                                            setDeleteLineCrossingDialogOpen(
                                              true
                                            );
                                          }
                                        }}
                                        text={<DeleteIcon fontSize="small" />}
                                        disabled={!line.id}
                                      />
                                    </span>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>
                  )}
                  {lines.length === 0 && (
                    <div
                      style={{
                        width: 400,
                        height: 300,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        paddingLeft: 120,
                        paddingTop: 35,
                      }}
                    >
                      <p className="font-mulish text-[18px]">
                        No line crossings added.
                      </p>
                    </div>
                  )}
                </div>
              )}
              {/* Message when Line Crossing product is not enabled */}
              {!editorVisible && !lineCrossingProductEnabled && (
                <div
                  className={
                    !navBarCollapsed
                      ? "absolute left-[775px] top-[53px]"
                      : "absolute left-[680px] top-[53px]"
                  }
                  style={{
                    width: 400,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    gap: "16px",
                  }}
                >
                  <p className="font-mulish text-[18px] text-gray-700">
                    The Line Crossing product has not been added for this
                    camera.
                  </p>
                  <p className="font-mulish text-[18px] text-gray-700">
                    Please navigate to the{" "}
                    <a
                      href={`/sites/${siteId}/editcamera/${getNumbersAfterEditCamera(
                        location.href
                      )}`}
                      className="text-blue-600 hover:underline cursor-pointer"
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(
                          `/sites/${siteId}/editcamera/${getNumbersAfterEditCamera(
                            location.href
                          )}`
                        );
                      }}
                    >
                      Edit Camera
                    </a>{" "}
                    page and enable it under the Products tab.
                  </p>
                </div>
              )}
              {/* Editor mode - LineDrawer */}
              {editorVisible && (
                <div className="p-4 pt-3">
                  <div className="flex gap-4">
                    <div style={{ position: "relative" }}>
                      {/* Cancel and Save buttons - top right of LineDrawer */}
                      <div
                        style={{
                          position: "absolute",
                          top: -38,
                          left: 1100,
                          display: "flex",
                          width: "100%",
                          gap: "16px",
                        }}
                      >
                        <OrangeTextButton
                          onClick={handleCancelClick}
                          text="Cancel"
                          disabled={false}
                        />
                        <ActuateButton
                          onClick={handleSaveClick}
                          text="Save and Submit"
                          disabled={false}
                        />
                      </div>
                      <div className="bg-white p-4 rounded">
                        <LineDrawer
                        largerMode={largerMode}
                        setLargerMode={setLargerMode}
                      />
                      </div>
                    </div>
                    <DirectionSelector />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <div className="justify-start">
        <NavBar
          navBarCollapsed={navBarCollapsed}
          setNavBarCollapsed={setNavBarCollapsed}
          props={{}}
        />
      </div>
    </div>
  );
};
